import type { ReactNode } from "react";
import { Pressable, Text, View, type ViewStyle } from "react-native";
import { Check, X, type LucideIcon } from "lucide-react-native";
import { colors, typography } from "../theme/tokens";
import { getInteractionDelta } from "../utils/interactionStyles";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

/**
 * Selectable pill for filter rows. Audit task 2.5.
 *
 * Canonicalises the GrantsFilterModal chip shape (`rounded-full` pill,
 * deep-brown tinted background when selected, optional check icon). LearnScreen
 * uses a near-identical shape; both should converge on this in the sweep.
 *
 * Variants supported:
 *   - bare label (GrantsFilterModal default)
 *   - label + Check on selected (`showCheck`, default true)
 *   - label + leading icon (`icon`, e.g. AddMedicationModal time-of-day,
 *     LearnScreen topic icons) — icon stays visible in both states,
 *     tinting follows the selected state
 *   - compact (`size="sm"`), for dense rows such as a chart legend
 *   - higher contrast on light glass cards (`tone="outline"`)
 *
 * State coverage (Phase 1.0 matrix):
 *   - idle / selected — bg + border swap, optional check icon
 *   - disabled        — opacity 0.5, presses ignored
 *   - error / focused / loading — N/A; filter chips don't sit inside
 *     FormField chrome and don't carry per-chip validation.
 */
/**
 * Visual context for the chip:
 *   - "default": dark-on-light. Lives inside modals / solid panels where
 *     the page bg is neutral. Inactive uses subtle dark tint, active uses
 *     accent-tinted bg.
 *   - "glass": light-on-glass. Lives on glassy scrolling lists (LearnScreen,
 *     GrantsScreen, FindCareScreen) where the page bg is the warm cream
 *     gradient. Inactive uses white-translucent, active uses a darker
 *     brown tint that reads against the glass.
 *   - "outline": higher contrast, for chips that sit on a light glass card
 *     (medication side effects, Care condition suggestions), where the glass
 *     tone's white-on-white fades into the card. Inactive is a near-solid
 *     white fill with a clear warm-brown outline and full-strength medium
 *     text; active takes an accent tint, an accent outline at half strength
 *     and a semibold accent label, so the two states read apart at a glance.
 */
export type MoonFilterChipTone = "default" | "glass" | "outline";

/**
 * Chip size:
 *   - "md": the standard chip.
 *   - "sm": compact, with less padding and smaller text, for dense rows such
 *     as a chart legend.
 */
export type MoonFilterChipSize = "md" | "sm";

const INACTIVE_SURFACE: Record<
  MoonFilterChipTone,
  { background: string; border: string; text: string }
> = {
  default: {
    background: "rgba(0, 0, 0, 0.04)",
    border: "rgba(0, 0, 0, 0.06)",
    text: colors.textMid,
  },
  glass: {
    background: "rgba(255, 255, 255, 0.4)",
    border: "rgba(255, 255, 255, 0.5)",
    text: colors.textMid,
  },
  outline: {
    background: "rgba(255, 255, 255, 0.78)",
    border: "rgba(92, 52, 32, 0.22)",
    text: colors.text,
  },
};

const SIZE_METRICS: Record<
  MoonFilterChipSize,
  {
    paddingHorizontal: number;
    paddingVertical: Record<MoonFilterChipTone, number>;
    fontSize: number;
    lineHeight?: number;
    iconSize: number;
    checkSize: number;
    gap: number;
  }
> = {
  md: {
    paddingHorizontal: 14,
    paddingVertical: { default: 9, glass: 7, outline: 8 },
    fontSize: 13,
    iconSize: 14,
    checkSize: 13,
    gap: 6,
  },
  sm: {
    paddingHorizontal: 10,
    paddingVertical: { default: 5, glass: 5, outline: 5 },
    fontSize: 11.5,
    lineHeight: 15,
    iconSize: 12,
    checkSize: 11,
    gap: 5,
  },
};

type Props = {
  label: string;
  selected: boolean;
  onPress: () => void;
  /** Stable analytics identifier, required when `label` is translated. */
  action?: string;
  /** Hue used for the selected bg/border tint. Defaults to deep brown. */
  accentColor?: string;
  /** Show a check icon on the left when selected. Defaults to true. */
  showCheck?: boolean;
  /** Accessible name override for chips with additional context. */
  accessibilityLabel?: string;
  /** Leading icon (always visible). Tints with selection. */
  icon?: LucideIcon;
  /**
   * Leading visual that a Lucide icon cannot express, such as a chart
   * legend's colour swatch. Rendered as given, before the label.
   */
  leading?: ReactNode;
  /**
   * Trailing control with its own action, such as a quick log toggle beside a
   * chip whose body opens details. Rendered as given, after the label; it owns
   * its press and analytics (use IconButton).
   */
  trailing?: ReactNode;
  /** Corner radius override. Defaults to 20 (pill). Use 10 for the
   *  square-ish AddMedicationModal time-of-day chip variant. */
  radius?: number;
  /** Visual context. See type doc. Defaults to "default". */
  tone?: MoonFilterChipTone;
  /** Size. See type doc. Defaults to "md". */
  size?: MoonFilterChipSize;
  disabled?: boolean;
  testID?: string;
  style?: ViewStyle;
  /**
   * When set, the chip renders a trailing X that calls this instead of the
   * check icon. Turns the chip into a removable active-filter token (the chip
   * body still fires onPress). Accessible name for the X override below.
   */
  onRemove?: () => void;
  removeAccessibilityLabel?: string;
};

export function MoonFilterChip({
  label,
  selected,
  onPress,
  action,
  accentColor,
  showCheck = true,
  accessibilityLabel,
  icon: Icon,
  leading,
  trailing,
  radius = 20,
  tone = "default",
  size = "md",
  disabled,
  testID,
  style,
  onRemove,
  removeAccessibilityLabel,
}: Props) {
  const capturePress = usePressCapture("MoonFilterChip");
  const isOutline = tone === "outline";
  const accent = accentColor ?? colors.deepBrown;
  const inactive = INACTIVE_SURFACE[tone];
  const metrics = SIZE_METRICS[size];
  // The outline tone marks its selected state harder, since its idle state
  // already carries a visible outline.
  const selectedBg = isOutline ? `${accent}1A` : `${accent}18`;
  const selectedBorder = isOutline ? `${accent}80` : `${accent}30`;
  const tint = selected ? accent : inactive.text;
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityState={{ selected, disabled }}
      // Inert testability hook: react-native-web 0.21 no longer forwards
      // accessibilityState.selected to the DOM, so mirror the selected state
      // via the aria-selected prop (RN forwards it, RNW renders it as the
      // aria-selected attribute) for deterministic E2E assertions. Purely
      // observational — does not affect behavior.
      aria-selected={selected}
      onPress={() => {
        capturePress(pressLabel(label, accessibilityLabel), action);
        onPress();
      }}
      disabled={disabled}
      testID={testID}
      // A long label wraps inside the chip instead of running past its row.
      style={{ maxWidth: "100%" }}
    >
      {({ hovered, focused }) => (
        <View
          style={[
            {
              flexDirection: "row",
              alignItems: "center",
              flexShrink: 1,
              paddingHorizontal: metrics.paddingHorizontal,
              paddingVertical: metrics.paddingVertical[tone],
              borderRadius: radius,
              borderWidth: 1,
              backgroundColor: selected ? selectedBg : inactive.background,
              borderColor: selected ? selectedBorder : inactive.border,
              opacity: disabled ? 0.7 : 1,
            },
            style,
            getInteractionDelta({ hovered, focused }),
          ]}
        >
          {Icon ? (
            <Icon
              size={metrics.iconSize}
              color={tint}
              style={{ marginRight: metrics.gap }}
            />
          ) : null}
          {leading ? (
            <View style={{ marginRight: metrics.gap }}>{leading}</View>
          ) : null}
          {/* A chip with its own icon is marked by its fill; a check beside the
              icon would give it two leading symbols. */}
          {selected && showCheck && !onRemove && !Icon ? (
            <View
              style={{
                marginRight: metrics.gap,
                marginLeft: Icon ? 0 : -2,
              }}
            >
              <Check
                size={metrics.checkSize}
                color={accent}
                strokeWidth={2.5}
              />
            </View>
          ) : null}
          <Text
            style={{
              fontFamily:
                selected || isOutline
                  ? typography.families.sansMedium
                  : typography.families.sans,
              fontSize: metrics.fontSize,
              ...(metrics.lineHeight
                ? { lineHeight: metrics.lineHeight }
                : null),
              fontWeight: selected ? "600" : isOutline ? "500" : "400",
              color: tint,
              flexShrink: 1,
            }}
          >
            {label}
          </Text>
          {trailing ? (
            <View style={{ marginLeft: metrics.gap, marginRight: -4 }}>
              {trailing}
            </View>
          ) : null}
          {onRemove ? (
            <Pressable
              onPress={() => {
                capturePress("filter_chip_remove", "filter_chip_remove");
                onRemove();
              }}
              hitSlop={8}
              accessibilityRole="button"
              accessibilityLabel={removeAccessibilityLabel}
              style={{ marginLeft: metrics.gap, marginRight: -2 }}
            >
              <X size={metrics.checkSize} color={tint} strokeWidth={2} />
            </Pressable>
          ) : null}
        </View>
      )}
    </Pressable>
  );
}
